import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Paper,
  Button,
  Chip,
  Stack,
  Tabs,
  Tab,
  Tooltip,
  useTheme,
  useMediaQuery,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  IconButton,
  Collapse,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  AddShoppingCart,
  SupportAgent,
  CalendarMonth,
  Link as LinkIcon,
  DeleteOutline,
  ExpandMore,
} from "@mui/icons-material";
import { useClientDetails } from "../../hooks/useClientsDetails";
import { usePageContext } from "../../context/PageContext";
import { fetchData } from "../../api/client";
import { getSelectedCompanyIds } from "../../utils/workspaceStorage";
import { useClientRelationships } from "../../hooks/useClientRelationships";
import ClientSearchOption from "../../components/clients/ClientSearchOption";
import { useClients } from "../../hooks/useClients";
import DuplicateClientDialog from "../../components/clients/DuplicateClientDialog";
import ExternalRegistrationButton from "../../components/clients/ExternalRegistrationButton";
import ClientPortalSection from "../../components/clients/ClientPortalSection";
import { useAuthContext } from "../../context/AuthContext";
import {
  can,
  scopeSetFromPermissions,
  useEffectivePermissions,
} from "../../hooks/useEffectivePermissions";
import ApiErrorAlertBody, { resolveApiErrorNotice } from "../../components/ApiErrorAlertBody";
import {
  InfoField,
  InfoFieldsGrid,
  ContactCard,
  KpiCard,
  RequestsOpenClosedBar,
  ClientRequestsList,
  ClientPurchaseList,
  ClientSurveyList,
  ClientDetailHeader,
  TabPanel,
} from "../../components/clients/ClientDetailSections";
import { alpha } from "@mui/material/styles";
import { getStatusChip } from "../../utils/utils";
import { formatPhoneDisplay, sanitizeStoredPhoneForDisplay } from "../../utils/phone";
import { Paid, ShoppingCart, SupportAgent as SupportAgentOutlined } from "@mui/icons-material";
import {
  getClientCommercialName,
  getNomeFantasia,
  getRazaoSocial,
  isPessoaJuridica,
} from "../../utils/clientName";

function getFirstSelectedProjectId() {
  const ids = getSelectedCompanyIds();
  return ids.length ? Number(ids[0]) : undefined;
}

/** Helpers */
const tipoNumToStr = (v) => (v === 1 ? "Pessoa Jurídica" : v === 0 ? "Pessoa Física" : "");
const statusNumToStr = (v) => (v === 1 ? "Ativo" : v === 0 ? "Inativo" : "");
const money = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleString("pt-BR");
};
const splitTags = (t) =>
  (t ? String(t).split(/[;,]/).map((x) => x.trim()).filter(Boolean) : []);

/** ====== Status Solicitação (0..5) ====== */
const REQ_STATUS_MAP = {
  0: "Pendente",
  1: "Em atendimento",
  2: "Concluída",
  3: "Contato futuro",
  4: "Perdida",
  5: "Cancelada",
};

const parseReqStatus = (s) => {
  if (s === null || s === undefined) return undefined;
  if (typeof s === "number" && Number.isFinite(s)) return s;
  const str = String(s).trim().toLowerCase();
  const n = Number(str);
  if (!isNaN(n) && n in REQ_STATUS_MAP) return n;
  const found = Object.entries(REQ_STATUS_MAP).find(([, label]) => label.toLowerCase() === str);
  return found ? Number(found[0]) : undefined;
};

const getReqStatusLabel = (s) => {
  const code = parseReqStatus(s);
  return code !== undefined ? REQ_STATUS_MAP[code] : "—";
};

const getReqStatusChipColor = (s) => {
  const code = parseReqStatus(s);
  switch (code) {
    case 0:
      return "warning";
    case 1:
      return "info";
    case 2:
      return "success";
    case 3:
      return "default";
    case 4:
      return "default";
    case 5:
      return "error";
    default:
      return "default";
  }
};

const isOpenStatus = (s) => {
  const code = parseReqStatus(s);
  return code === 0 || code === 1 || code === 3;
};
const isClosedStatus = (s) => {
  const code = parseReqStatus(s);
  return code === 2 || code === 4 || code === 5;
};

/** ====== Tipo / Subtipos – tenta usar nomes, cai para IDs se não tiver ====== */
const buildSaleTypePath = (p) => {
  if (!p) return "—";

  const parts = [];

  const typeName = p.typeName || p.tipo_nome || p.tipoName;
  const s1Name = p.subtype1Name || p.subtipo1_nome;
  const s2Name = p.subtype2Name || p.subtipo2_nome;
  const s3Name = p.subtype3Name || p.subtipo3_nome;

  if (typeName) parts.push(typeName);
  if (s1Name) parts.push(s1Name);
  if (s2Name) parts.push(s2Name);
  if (s3Name) parts.push(s3Name);

  // se já tiver nomes, usa só eles
  if (parts.length) return parts.join(" • ");

  // fallback: mostra IDs como estava antes
  const ids = [];
  if (p.id_tipo != null) ids.push(`T${p.id_tipo}`);
  if (p.id_subtipo1 != null) ids.push(`S1-${p.id_subtipo1}`);
  if (p.id_subtipo2 != null) ids.push(`S2-${p.id_subtipo2}`);
  if (p.id_subtipo3 != null) ids.push(`S3-${p.id_subtipo3}`);
  return ids.length ? ids.join(" • ") : "—";
};

const buildProductsSummary = (p) => {
  if (!p) return "—";

  // se vier o array de itens do backend (items_json -> items)
  if (Array.isArray(p.items) && p.items.length > 0) {
    const first = p.items[0];
    const name = first.name || first.nome || first.product || p.product || "—";
    const qty = first.qty || first.quantidade || 1;
    const extra = p.items.length > 1 ? ` + ${p.items.length - 1} produto(s)` : "";
    return `${qty}× ${name}${extra}`;
  }

  // fallback: usa campo product textual
  return p.product || "—";
};

const getOrderCode = (p) => {
  if (!p) return "—";
  if (p.orderCode) return p.orderCode;
  if (p.codigo) return p.codigo;
  return `#${p.id}`;
};

/** ID da pesquisa (definição), não da sessão de resposta — vem do GET /clients/:id/details */
const getSurveyDefinitionId = (s) => s?.survey_id ?? s?.surveyId ?? null;

/** ====== Linha do tempo — unificação (rótulos amigáveis) ====== */
const MERGE_FIELD_LABELS_PT = {
  nome: "Nome",
  apelido: "Apelido",
  email: "E-mail",
  telefone: "Telefone",
  celular: "Celular",
  whatsapp: "WhatsApp",
  cpf: "CPF",
  cnpj: "CNPJ",
  razaosocial: "Razão social",
  razao_social: "Razão social",
  endereco: "Endereço",
  numero: "Número",
  complemento: "Complemento",
  bairro: "Bairro",
  cidade: "Cidade",
  uf: "UF",
  cep: "CEP",
  tags: "Tags",
  id_grupo: "Nível de consciência (grupo)",
  potencial: "Potencial",
  imagem: "Foto do perfil",
  biografia: "Biografia",
  website: "Site",
  profissao: "Profissão",
  cargo: "Cargo",
  sexo: "Sexo",
  data_nascimento: "Data de nascimento",
  identidade: "RG / Identidade",
  data_abertura: "Data de abertura",
  inscricao_estadual: "Inscrição estadual",
  inscricao_estadual_isento: "Isento de IE",
  nome_responsavel: "Nome do responsável",
  cargo_responsavel: "Cargo do responsável",
  tamanho_frota: "Tamanho da frota",
  estado_civil: "Estado civil",
  id_interno: "ID interno",
  observacao: "Observações",
};

const mergeFieldLabelPt = (key) => {
  if (!key) return "";
  if (MERGE_FIELD_LABELS_PT[key]) return MERGE_FIELD_LABELS_PT[key];
  const s = String(key).replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const formatMergeValue = (v) => {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 240);
  const s = String(v).trim();
  if (!s) return "—";
  if (s.length > 120) return `${s.slice(0, 117)}…`;
  return s;
};

const MERGE_TIMELINE_TECH_FIELDS = new Set([
  "observacao",
  "data_ultima_alteracao",
  "id_usuario_ultima_alteracao",
  "data_cadastro",
  "excluido",
]);

function MergeTimelineEvent({ event: t }) {
  const [open, setOpen] = useState(false);
  const resolved = Array.isArray(t?.meta?.resolvedEntries) ? t.meta.resolvedEntries : [];
  const auto = Array.isArray(t?.meta?.autoLabels) ? t.meta.autoLabels : [];

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip size="small" label={t.type} color="info" variant="outlined" />
          <Typography variant="subtitle2" sx={{ mr: 1, flex: 1, minWidth: 200 }}>
            {t.title}
          </Typography>
          <Tooltip title="Data do evento">
            <CalendarMonth fontSize="small" />
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            {fmtDate(t.date)}
          </Typography>
          {t.status ? (
            <Chip size="small" label={t.status} color="default" variant="outlined" />
          ) : null}
        </Stack>

        {(resolved.length > 0 || auto.length > 0) && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              size="small"
              onClick={() => setOpen((v) => !v)}
              endIcon={
                <ExpandMore
                  sx={{
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 150ms ease",
                  }}
                />
              }
              sx={{ textTransform: "none", px: 0.5 }}
            >
              {open ? "Ocultar detalhes" : "Ver detalhes"}
            </Button>
            {!open && resolved.length ? (
              <Typography variant="caption" color="text.secondary">
                {resolved.length} campo(s) escolhido(s)
                {auto.length ? ` · ${auto.length} preenchido(s) automaticamente` : ""}
              </Typography>
            ) : null}
          </Stack>
        )}

        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
            {resolved.length ? (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                  Escolhidos na unificação
                </Typography>
                <Stack spacing={0.5}>
                  {resolved.map((row) => (
                    <Typography key={row.key} variant="body2">
                      <strong>{row.label}:</strong> {row.value}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            ) : null}
            {auto.length ? (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                  Preenchidos automaticamente (cadastro principal estava vazio)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {auto.join(", ")}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}

/** ====== Estilos ====== */
const cardBaseSx = {
  borderRadius: 2.5,
  width: "100%",
  boxSizing: "border-box",
  "& .MuiCardContent-root": { py: 2.5, px: 2.5 },
};

const metricsGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(4, minmax(0, 1fr))",
  },
  gap: 2,
  mb: 3,
  alignItems: "stretch",
};

const detailGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
  gap: 2,
  mb: 3,
  alignItems: "start",
};
const sectionTitleSx = { mb: 1, fontWeight: 700, letterSpacing: 0.2 };

const pageShellSx = {
  width: "100%",
  maxWidth: "none",
  minWidth: 0,
  boxSizing: "border-box",
  p: { xs: 2, md: 2.5, lg: 3 },
};

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, error, refetch } = useClientDetails(id);
  const { currentPage } = usePageContext();
  const { userData } = useAuthContext();
  const { data: permissionsData } = useEffectivePermissions();
  const permissionScopes = scopeSetFromPermissions(permissionsData, userData);
  const canManageClients = can(permissionScopes, "clients", "manage");
  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const downMd = useMediaQuery(theme.breakpoints.down("md"));

  // Estados de criação de solicitações
  const [creatingRel, setCreatingRel] = useState(false);
  const [creatingSale, setCreatingSale] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [saveNotice, setSaveNotice] = useState(null);
  const [dupDialog, setDupDialog] = useState({
    open: false,
    duplicates: [],
    attemptedPayload: null,
    secondaryClientId: null,
  });
  const [relDialogOpen, setRelDialogOpen] = useState(false);
  const [relType, setRelType] = useState("outro");
  const [relBidirectional, setRelBidirectional] = useState(true);
  const [relNotes, setRelNotes] = useState("");
  const [relClientSearch, setRelClientSearch] = useState("");
  const [relSelectedClient, setRelSelectedClient] = useState(null);
  const [relError, setRelError] = useState(null);

  // Normalizações
  const client = useMemo(() => data?.client ?? {}, [data]);
  const requests = useMemo(() => data?.requests ?? [], [data]);
  const purchases = useMemo(() => data?.purchases ?? [], [data]);
  const surveys = useMemo(() => data?.surveys ?? [], [data]);

  const _tipo = useMemo(
    () => (typeof client.tipo === "number" ? tipoNumToStr(client.tipo) : client.tipo || "—"),
    [client.tipo]
  );
  const _status = useMemo(
    () => (typeof client.status === "number" ? statusNumToStr(client.status) : client.status || "—"),
    [client.status]
  );
  const _isPJ = useMemo(() => isPessoaJuridica(client), [client]);

  const nome = useMemo(
    () => getClientCommercialName(client) || "Nome não disponível",
    [client]
  );
  const nomeFantasia = useMemo(() => {
    if (!_isPJ) return null;
    const fantasia = getNomeFantasia(client);
    const razao = getRazaoSocial(client);
    // Se `nome` só espelha a razão, não há fantasia distinta.
    if (!fantasia || (razao && fantasia === razao)) return "—";
    return fantasia;
  }, [client, _isPJ]);
  const nomeSubtitle = useMemo(() => {
    const extra = client.apelido || client.grupo_nome || client.potencial;
    return extra ? String(extra).trim() : null;
  }, [client.apelido, client.grupo_nome, client.potencial]);
  const clientStatusActive = useMemo(() => {
    const s = client.status;
    if (typeof s === "number") return s === 1;
    return String(s || "").toLowerCase() === "ativo";
  }, [client.status]);
  const email = client.email || "Não informado";
  const cidade = client.cidade || "Não informado";
  const estado = client.uf || "Não informado";
  const imagem = client.imagem || "";
  const razaoSocial = getRazaoSocial(client) || "—";
  const cnpj = client.cnpj || "—";
  const cpf = client.cpf || "—";
  const dataNasc = client.data_nascimento ? fmtDate(client.data_nascimento) : "—";
  const identidade = client.identidade || client.rg || "—";
  const dataAbertura = client.data_abertura ? fmtDate(client.data_abertura) : "—";
  const inscricaoEstadual = Number(client.inscricao_estadual_isento)
    ? "Isento"
    : client.inscricao_estadual || "—";
  const nomeResponsavel = client.nome_responsavel || "—";
  const cargoResponsavel = client.cargo_responsavel || "—";
  const tamanhoFrota = client.tamanho_frota || "—";
  const sexo = client.sexo || "—";
  const estadoCivil = client.estado_civil || "—";
  const telefone = sanitizeStoredPhoneForDisplay(client.telefone)
    ? formatPhoneDisplay(client.telefone)
    : "—";
  const whatsappRaw =
    sanitizeStoredPhoneForDisplay(client.whatsapp) ||
    sanitizeStoredPhoneForDisplay(client.numero_whatsapp);
  const whatsapp = whatsappRaw ? formatPhoneDisplay(whatsappRaw) : "—";
  const linkedin = client.linkedin || client.id_linkedin || "";
  const website = client.website || "";
  const tags = useMemo(() => splitTags(client.tags), [client.tags]);
  const origem = client.origem;
  const endereco = {
    cep: client.cep,
    logradouro: client.endereco,
    numero: client.numero,
    bairro: client.bairro,
    cidade: client.cidade,
    uf: client.uf,
    complemento: client.complemento,
  };

  // KPIs
  const { purchasesCount, purchasesTotal, purchasesAvg, lastPurchaseDate, openReq, closedReq } = useMemo(() => {
    const visiblePurchases = purchases.filter((p) => !p.restricted);
    const count = visiblePurchases.length;
    const total = visiblePurchases.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
    const avg = count ? total / count : 0;
    const lastDate = visiblePurchases.reduce((acc, p) => {
      const d = new Date(p.date);
      return isNaN(d.getTime()) ? acc : (!acc || d > acc ? d : acc);
    }, null);
    const open = requests.filter((r) => !r.restricted && isOpenStatus(r.status)).length;
    const closed = requests.filter((r) => !r.restricted && isClosedStatus(r.status)).length;

    return {
      purchasesCount: count,
      purchasesTotal: total,
      purchasesAvg: avg,
      lastPurchaseDate: lastDate ? fmtDate(lastDate) : "—",
      openReq: open,
      closedReq: closed,
    };
  }, [purchases, requests]);

  // Timeline
  const timeline = useMemo(() => {
    const items = [
      ...(String(client?.observacao || "")
        .split("\n")
        .map((line) => String(line || "").trim())
        .filter((line) => line.includes("Unificação de cliente:"))
        .map((line, idx) => {
          const m = line.match(/^\[(.+?)\]\s+Unificação de cliente:\s+(.*)$/);
          const date = m?.[1] || null;
          const rawJson = m?.[2] || "";
          let details = null;
          try {
            details = rawJson ? JSON.parse(rawJson) : null;
          } catch {
            details = null;
          }
          const secId = details?.secondary?.id ?? details?.secondaryId ?? null;
          const primId = details?.primary?.id ?? details?.primaryId ?? client?.id ?? null;
          const secName = details?.secondary?.nome || null;
          const primName = details?.primary?.nome || null;
          const resolvedFieldsObj =
            details?.resolvedFields && typeof details.resolvedFields === "object"
              ? details.resolvedFields
              : {};
          const resolvedKeys = Array.isArray(details?.resolvedFieldsList)
            ? details.resolvedFieldsList
            : Object.keys(resolvedFieldsObj);
          const resolvedEntries = resolvedKeys.map((key) => ({
            key,
            label: mergeFieldLabelPt(key),
            value: formatMergeValue(
              Object.prototype.hasOwnProperty.call(resolvedFieldsObj, key)
                ? resolvedFieldsObj[key]
                : undefined
            ),
          }));

          const autoRaw = Array.isArray(details?.autoCopied) ? details.autoCopied : [];
          const autoLabels = Array.from(
            new Set(
              autoRaw
                .filter((k) => k && !MERGE_TIMELINE_TECH_FIELDS.has(String(k)))
                .map((k) => mergeFieldLabelPt(String(k)))
            )
          );

          const title =
            secId != null && primId != null
              ? `Cliente #${secId}${secName ? ` (${secName})` : ""} unificado em #${primId}${primName ? ` (${primName})` : ""}`
              : "Unificação de cliente";

          const statusParts = [];
          if (details?.by) statusParts.push(details.by);
          const status = statusParts.length ? `Por ${statusParts.join(" • ")}` : "Unificação";
          return {
            type: "Unificação",
            date,
            title,
            status,
            statusColor: "info",
            meta: {
              resolvedEntries,
              autoLabels,
            },
            id: `merge-${client?.id || "x"}-${idx}`,
          };
        })),
      ...requests.map((r) => ({
        type: "Solicitação",
        date: r.lastUpdate || r.data || r.createdAt || r.date,
        title: r.restricted
          ? r.subject || buildSaleTypePath(r) || "Tipo restrito"
          : r.subject || "—",
        status: r.restricted ? "Sem permissão" : getReqStatusLabel(r.status),
        statusCode: r.restricted ? null : r.status,
        statusColor: r.restricted ? "warning" : getReqStatusChipColor(r.status),
        restricted: Boolean(r.restricted),
        id: r.restricted ? `restricted-req-${r.id}` : r.id,
        ...(r.restricted ? {} : { linkTo: "chat", linkId: r.id }),
      })),
      ...purchases.map((p) => ({
        type: "Compra",
        date: p.date || p.data || p.createdAt,
        title: p.restricted
          ? p.subject || p.typePath || buildSaleTypePath(p) || "Tipo restrito"
          : p.product || "—",
        status: p.restricted ? "Sem permissão" : p.amount ? money(p.amount) : "—",
        restricted: Boolean(p.restricted),
        id: p.restricted ? `restricted-buy-${p.id}` : p.id,
        ...(p.restricted ? {} : { linkTo: "pipeline", linkId: p.id }),
      })),
      ...surveys.map((s) => {
        const defId = getSurveyDefinitionId(s);
        return {
          type: "Pesquisa",
          date: s.responseDate || s.data || s.createdAt,
          title: s.title || "—",
          status: "Respondida",
          id: s.id,
          ...(defId != null ? { linkTo: "survey", linkId: defId } : {}),
        };
      }),
    ].filter((x) => x.date);

    items.sort((a, b) => new Date(b.date) - new Date(a.date));
    return items.slice(0, 20);
  }, [client?.observacao, client?.id, requests, purchases, surveys]);

  const {
    data: relationships = [],
    isLoading: isRelationshipsLoading,
    createMutation: createRelationshipMutation,
    deleteMutation: deleteRelationshipMutation,
  } = useClientRelationships(id);

  const { data: relatedCandidatesData } = useClients(
    1,
    20,
    relClientSearch,
    client?.id_projeto || null,
    { enabled: relDialogOpen && relClientSearch.trim().length >= 2 }
  );
  const relatedCandidates = (relatedCandidatesData?.clients || []).filter(
    (c) => Number(c.id) !== Number(client?.id)
  );

  const relationshipTypeOptions = [
    { value: "familiar", label: "Familiar" },
    { value: "conjuge", label: "Cônjuge" },
    { value: "responsavel", label: "Responsável" },
    { value: "dependente", label: "Dependente" },
    { value: "socio", label: "Sócio" },
    { value: "contato_comercial", label: "Contato comercial" },
    { value: "indicador", label: "Indicador" },
    { value: "funcionario", label: "Funcionário" },
    { value: "outro", label: "Outro" },
  ];

  const relationshipTypeLabel = (value) =>
    relationshipTypeOptions.find((o) => o.value === value)?.label || value || "Outro";

  const openRelDialog = () => {
    setRelDialogOpen(true);
    setRelError(null);
    setRelType("outro");
    setRelBidirectional(true);
    setRelNotes("");
    setRelSelectedClient(null);
    setRelClientSearch("");
  };

  const closeRelDialog = () => {
    if (createRelationshipMutation.isLoading) return;
    setRelDialogOpen(false);
  };

  const handleCreateRelationship = async () => {
    if (!relSelectedClient?.id) {
      setRelError("Selecione um cliente para vincular.");
      return;
    }
    try {
      setRelError(null);
      await createRelationshipMutation.mutateAsync({
        related_client_id: relSelectedClient.id,
        relationship_type: relType,
        is_bidirectional: relBidirectional,
        notes: relNotes,
      });
      closeRelDialog();
    } catch (e) {
      setRelError(
        e?.response?.data?.message || e?.message || "Não foi possível criar o relacionamento."
      );
    }
  };

  const handleDeleteRelationship = async (relationshipId) => {
    try {
      await deleteRelationshipMutation.mutateAsync(relationshipId);
    } catch (_) {
      // Erro não bloqueante: mantém UX simples.
    }
  };

  const handleOpenSaleInPipeline = (purchase) => {
    if (!purchase || purchase.restricted) return;
    const id = purchase.pipelineId || purchase.saleId || purchase.id;
    if (!id) return;
    navigate(`/sales-pipeline/${id}`);
  };

  const handleOpenSurveyAnalytics = (s) => {
    const surveyId = getSurveyDefinitionId(s);
    if (!surveyId) return;
    navigate(`/surveys/${surveyId}/analytics`);
  };

  const handleTimelineItemNavigate = (t) => {
    if (!t?.linkTo) return;
    if (t.linkTo === "chat" && t.linkId != null) {
      navigate(`/chat/${encodeURIComponent(String(t.linkId))}`);
      return;
    }
    if (t.linkTo === "pipeline" && t.linkId != null) {
      navigate(`/sales-pipeline/${t.linkId}`);
      return;
    }
    if (t.linkTo === "survey" && t.linkId != null) {
      navigate(`/surveys/${t.linkId}/analytics`);
    }
  };

  const handleOpenRelatedClient = (rel) => {
    if (!rel || !client?.id) return;
    const isFrom = Number(rel.client_id_from) === Number(client.id);
    const otherId = isFrom ? rel.client_id_to : rel.client_id_from;
    if (otherId == null) return;
    navigate(`/clients/${otherId}`);
  };

  const getProjectIdFromSession = getFirstSelectedProjectId;

  const openPopup = (conversationId) => {
    const width = window.screen.width;
    const height = window.screen.height;
    window.open(
      `/popup-call/${conversationId}`,
      "SolicitaçãoPopup",
      `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
    );
  };

  // Navegação / edição
  const handleBack = () => navigate(`/clients?page=${currentPage}`);

  const openClientForEdit = () => {
    if (!client?.id) return;
    navigate(`/clients/${client.id}/edit`);
  };

  useEffect(() => {
    if (searchParams.get("updated") === "1") {
      setSaveNotice("Cadastro atualizado com sucesso.");
      refetch?.();
      const next = new URLSearchParams(searchParams);
      next.delete("updated");
      setSearchParams(next, { replace: true });
    } else if (searchParams.get("merged") === "1") {
      setSaveNotice("Clientes unificados com sucesso.");
      refetch?.();
      const next = new URLSearchParams(searchParams);
      next.delete("merged");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, refetch]);

  // Novas solicitações
  const onNewRequest = async () => {
    if (!client?.id || creatingRel || creatingSale) return;
    try {
      setCreateError(null);
      setCreatingRel(true);

      const projectId = getProjectIdFromSession();
      const payload = {
        projectId: projectId || undefined,
        clientId: client.id,
        client: { id: client.id },
        typeId: undefined,
        typeKind: "1", // relacionamento
        initialMessage: "",
        source: "client-details",
      };

      const created = await fetchData("/conversations", "POST", payload);
      const conv = created?.data || created;
      if (!conv?.id) throw new Error("A API não retornou o ID da solicitação criada.");

      openPopup(conv.id);
    } catch (e) {
      const notice = resolveApiErrorNotice(
        e,
        "Não foi possível criar a solicitação."
      );
      setCreateError(notice);
    } finally {
      setCreatingRel(false);
    }
  };

  const onNewPurchase = async () => {
    if (!client?.id || creatingRel || creatingSale) return;
    try {
      setCreateError(null);
      setCreatingSale(true);

      const projectId = getProjectIdFromSession();
      const payload = {
        projectId: projectId || undefined,
        clientId: client.id,
        client: { id: client.id },
        typeId: undefined,
        typeKind: "2", // comercial (venda)
        initialMessage: "",
        source: "client-details",
      };

      const created = await fetchData("/conversations", "POST", payload);
      const conv = created?.data || created;
      if (!conv?.id) throw new Error("A API não retornou o ID da compra/solicitação criada.");

      openPopup(conv.id);
    } catch (e) {
      const notice = resolveApiErrorNotice(
        e,
        "Não foi possível criar a venda."
      );
      setCreateError(notice);
    } finally {
      setCreatingSale(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <Typography color="error" variant="h6">
          Erro ao carregar os detalhes do cliente. Por favor, tente novamente.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="tour-client-detail-root" sx={pageShellSx}>
      {saveNotice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveNotice(null)}>
          {saveNotice}
        </Alert>
      )}
      <Box className="tour-client-detail-header">
        <ClientDetailHeader
          nome={nome}
          nomeSubtitle={nomeSubtitle}
          imagem={imagem}
          clientId={client.id}
          tipo={_tipo}
          status={_status}
          statusActive={clientStatusActive}
          origem={origem}
          tags={tags}
          relationships={relationships}
          onBack={handleBack}
          onEdit={canManageClients ? openClientForEdit : undefined}
          isProcessingEdit={false}
          onLink={canManageClients ? openRelDialog : undefined}
          externalRegistrationSlot={
            canManageClients ? (
              <ExternalRegistrationButton
                idInterno={client?.id_interno}
                size="small"
                sx={{
                  ml: 0.5,
                  display: { xs: "none", lg: "inline-flex" },
                  fontSize: "0.7rem",
                  py: 0.75,
                  whiteSpace: "nowrap",
                }}
              />
            ) : null
          }
          onNewRequest={onNewRequest}
          onNewPurchase={onNewPurchase}
          creatingRel={creatingRel}
          creatingSale={creatingSale}
          downMd={downMd}
        />
      </Box>

      {createError && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Alert severity="error" onClose={() => setCreateError(null)} sx={{ alignItems: "flex-start" }}>
            {typeof createError === "string" ? (
              createError
            ) : (
              <ApiErrorAlertBody message={createError.message} action={createError.action} />
            )}
          </Alert>
        </Stack>
      )}

      {/* Métricas — CSS grid (evita sobreposição do MUI Grid + height 100%) */}
      <Box className="tour-client-detail-kpis" sx={metricsGridSx}>
        <KpiCard
          icon={<Paid fontSize="small" />}
          label="Valor total comprado"
          value={money(purchasesTotal)}
          hint={`Ticket médio: ${money(purchasesAvg)}`}
          accent="success"
          onClick={purchases.length ? () => setTab(1) : undefined}
        />
        <KpiCard
          icon={<ShoppingCart fontSize="small" />}
          label="Compras"
          value={String(purchasesCount)}
          hint={`Última: ${lastPurchaseDate}`}
          accent="primary"
          onClick={purchases.length ? () => setTab(1) : undefined}
        />
        <KpiCard
          icon={<SupportAgentOutlined fontSize="small" />}
          label="Solicitações"
          value={String(requests.length)}
          hint={`${openReq} em andamento`}
          accent="info"
          onClick={requests.length ? () => setTab(0) : undefined}
          footer={
            openReq > 0 || closedReq > 0 ? (
              <RequestsOpenClosedBar open={openReq} closed={closedReq} />
            ) : null
          }
        />
        <ContactCard
          email={email}
          telefone={telefone}
          whatsapp={whatsapp}
          cidade={cidade}
          estado={estado}
          website={website}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <ClientPortalSection
          clientId={client.id}
          email={client.email}
          acessoPortal={Number(client.acesso_portal_cliente)}
          hasPassword={Boolean(client.hasPassword)}
          onAcessoChange={async (v) => {
            try {
              await fetchData(`/clients/${id}`, "PUT", { acesso_portal_cliente: v });
              await refetch();
              setSaveNotice("Acesso ao portal atualizado.");
            } catch (e) {
              setCreateError(e?.message || "Erro ao atualizar portal.");
            }
          }}
        />
      </Box>

      {/* Identificação & Endereço */}
      <Box sx={detailGridSx}>
        <Card sx={cardBaseSx} elevation={0} variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={sectionTitleSx}>
              Identificação
            </Typography>
            <InfoFieldsGrid>
              {_isPJ ? (
                <>
                  <InfoField label="Razão Social" value={razaoSocial} />
                  <InfoField label="Nome fantasia" value={nomeFantasia} />
                  <InfoField label="CNPJ" value={cnpj} />
                  <InfoField label="Data de abertura" value={dataAbertura} />
                  <InfoField label="Inscrição estadual" value={inscricaoEstadual} />
                  <InfoField label="Responsável" value={nomeResponsavel} />
                  <InfoField label="Cargo do responsável" value={cargoResponsavel} />
                  <InfoField label="Tamanho da frota" value={tamanhoFrota} />
                </>
              ) : (
                <>
                  <InfoField label="CPF" value={cpf} />
                  <InfoField label="RG / Identidade" value={identidade} />
                  <InfoField label="Data de Nascimento" value={dataNasc} />
                  <InfoField label="Sexo" value={sexo} />
                  <InfoField label="Estado Civil" value={estadoCivil} />
                </>
              )}
              <InfoField label="Tipo" value={_tipo} />
              <InfoField label="Status" value={_status} />
              {client.id_interno && <InfoField label="ID interno" value={client.id_interno} />}
              {linkedin && <InfoField label="LinkedIn" value={linkedin} xs={12} />}
            </InfoFieldsGrid>
          </CardContent>
        </Card>

        <Card sx={cardBaseSx} elevation={0} variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={sectionTitleSx}>
              Endereço
            </Typography>
            <InfoFieldsGrid>
              <InfoField label="CEP" value={endereco.cep || "—"} xs={4} />
              <InfoField label="Logradouro" value={endereco.logradouro || "—"} xs={8} />
              <InfoField label="Número" value={endereco.numero || "—"} xs={4} />
              <InfoField label="Bairro" value={endereco.bairro || "—"} xs={8} />
              <InfoField
                label="Cidade / UF"
                value={
                  endereco.cidade || endereco.uf
                    ? `${endereco.cidade || "—"}${endereco.uf ? ` — ${endereco.uf}` : ""}`
                    : "—"
                }
                xs={12}
              />
              {endereco.complemento ? <InfoField label="Complemento" value={endereco.complemento} xs={12} /> : null}
            </InfoFieldsGrid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Tags
            </Typography>
            {tags.length ? (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {tags.map((t) => (
                  <Chip key={t} label={t} size="small" color="primary" variant="outlined" />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhuma tag cadastrada.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Abas */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2.5,
          overflow: "hidden",
          boxShadow: (t) => `0 1px 3px ${alpha(t.palette.common.black, 0.05)}`,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: { xs: 1, md: 2 },
            minHeight: 52,
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              fontWeight: 600,
              textTransform: "none",
              minHeight: 52,
              fontSize: "0.875rem",
              px: 2,
            },
            "& .Mui-selected": { color: "primary.main" },
          }}
        >
          <Tab label={`Solicitações (${requests.length})`} />
          <Tab label={`Compras (${purchases.length})`} />
          <Tab label={`Pesquisas (${surveys.length})`} />
          <Tab label="Linha do tempo" />
          <Tab label={`Relacionamentos (${relationships.length})`} />
        </Tabs>

        {tab === 0 && (
          <TabPanel>
            <ClientRequestsList
              requests={requests}
              clientName={nome}
              onRequestsChanged={refetch}
            />
          </TabPanel>
        )}

        {tab === 1 && (
          <TabPanel>
            <ClientPurchaseList
              purchases={purchases}
              onOpen={handleOpenSaleInPipeline}
              getOrderCode={getOrderCode}
              buildProductsSummary={buildProductsSummary}
              buildSaleTypePath={buildSaleTypePath}
            />
          </TabPanel>
        )}

        {tab === 2 && (
          <TabPanel>
            <ClientSurveyList
              surveys={surveys}
              onOpen={handleOpenSurveyAnalytics}
              fmtDate={fmtDate}
              getSurveyDefinitionId={getSurveyDefinitionId}
            />
          </TabPanel>
        )}

        {tab === 3 && (
          <TabPanel>
            {timeline.length ? (
              <Stack spacing={1.25}>
                {timeline.map((t) =>
                  t.type === "Unificação" ? (
                    <MergeTimelineEvent key={`${t.type}-${t.id}`} event={t} />
                  ) : (
                    <Paper
                      key={`${t.type}-${t.id}`}
                      variant="outlined"
                      onClick={() => handleTimelineItemNavigate(t)}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        ...(t.linkTo
                          ? {
                              cursor: "pointer",
                              transition: "box-shadow 120ms ease, border-color 120ms ease",
                              "&:hover": {
                                boxShadow: 1,
                                borderColor: "primary.light",
                              },
                            }
                          : {}),
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" label={t.type} />
                        <Typography variant="subtitle2" sx={{ mr: 1 }}>
                          {t.title}
                        </Typography>
                        <Tooltip title="Data do evento">
                          <CalendarMonth fontSize="small" />
                        </Tooltip>
                        <Typography variant="body2" color="text.secondary">
                          {fmtDate(t.date)}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        {t.status &&
                          (t.restricted ? (
                            <Chip size="small" label={t.status} color="warning" variant="outlined" />
                          ) : t.type === "Solicitação" ? (
                            getStatusChip(t.statusCode ?? t.status, theme) || (
                              <Chip size="small" label={t.status} color={t.statusColor || "default"} />
                            )
                          ) : (
                            <Chip size="small" label={t.status} color="default" variant="outlined" />
                          ))}
                      </Stack>
                    </Paper>
                  )
                )}
              </Stack>
            ) : (
              <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                Sem eventos recentes.
              </Typography>
            )}
          </TabPanel>
        )}
        {tab === 4 && (
          <TabPanel>
            {isRelationshipsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : relationships.length ? (
              <Stack spacing={1.25}>
                {relationships.map((rel) => {
                  const isFrom = Number(rel.client_id_from) === Number(client.id);
                  const otherName = isFrom ? rel.to_name : rel.from_name;
                  const direction = rel.is_bidirectional
                    ? "Bidirecional"
                    : isFrom
                    ? "Origem"
                    : "Destino";
                  return (
                    <Paper
                      key={rel.id}
                      variant="outlined"
                      onClick={() => handleOpenRelatedClient(rel)}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        cursor: "pointer",
                        transition: "box-shadow 120ms ease, border-color 120ms ease",
                        "&:hover": { boxShadow: 1, borderColor: "primary.light" },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" label={relationshipTypeLabel(rel.relationship_type)} />
                        <Chip size="small" variant="outlined" label={direction} />
                        <Typography variant="subtitle2">{otherName || `Cliente #${isFrom ? rel.client_id_to : rel.client_id_from}`}</Typography>
                        <Box sx={{ flex: 1 }} />
                        {canManageClients ? (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRelationship(rel.id);
                            }}
                            disabled={deleteRelationshipMutation.isLoading}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        ) : null}
                      </Stack>
                      {rel.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          {rel.notes}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              <Stack alignItems="center" spacing={1.25} sx={{ py: 2 }}>
                <Typography color="text.secondary">Nenhum relacionamento cadastrado.</Typography>
                {canManageClients ? (
                  <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={openRelDialog}>
                    Vincular relacionamento
                  </Button>
                ) : null}
              </Stack>
            )}
          </TabPanel>
        )}
      </Paper>

      <Dialog open={relDialogOpen} onClose={closeRelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Vincular relacionamento</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {relError && <Alert severity="error">{relError}</Alert>}
            <Autocomplete
              options={relatedCandidates}
              getOptionLabel={(o) => `${o?.nome || "Sem nome"} (#${o?.id || "-"})`}
              value={relSelectedClient}
              onChange={(_e, v) => setRelSelectedClient(v)}
              onInputChange={(_e, v) => setRelClientSearch(v || "")}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              loading={Boolean(relDialogOpen && relClientSearch.trim().length >= 2 && !relatedCandidates.length)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <ClientSearchOption
                    client={option}
                    primary={`${option?.nome || "Sem nome"} (#${option?.id || "-"})`}
                    secondary={option?.email}
                  />
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente relacionado"
                  placeholder="Digite nome, email, telefone..."
                  helperText="Pesquise por no mínimo 2 caracteres."
                />
              )}
            />
            <FormControl fullWidth>
              <InputLabel>Tipo de relacionamento</InputLabel>
              <Select
                label="Tipo de relacionamento"
                value={relType}
                onChange={(e) => setRelType(e.target.value)}
              >
                {relationshipTypeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Direção</InputLabel>
              <Select
                label="Direção"
                value={relBidirectional ? "bi" : "uni"}
                onChange={(e) => setRelBidirectional(e.target.value === "bi")}
              >
                <MenuItem value="bi">Bidirecional</MenuItem>
                <MenuItem value="uni">Unidirecional</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Observações"
              multiline
              minRows={3}
              value={relNotes}
              onChange={(e) => setRelNotes(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRelDialog} disabled={createRelationshipMutation.isLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateRelationship}
            disabled={createRelationshipMutation.isLoading}
          >
            {createRelationshipMutation.isLoading ? "Salvando..." : "Vincular"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDetails;
